import { AnimationCanvas, UIUtil } from '@suntex.core/animation';
import { $ArithmeticOperation, ArithmeticOperation } from '@suntex.fim/enums';

import { VIFSequenceLine } from './createjs/VIFSequenceLine.js';

const COMPOSITION_ID = '69884CA1BB592343A6C97E45FCE89689';

// Number of stages shown per operation (add, sub, mul, div).
const STAGES_BY_OPERATION = [21, 26, 21, 21];

// Frame indices in the Animate symbol: completed / current / locked.
const FRAME_COMPLETED = 1;
const FRAME_CURRENT = 2;
const FRAME_LOCKED = 3;

export interface VifSequenceLineProps {
  operation: ArithmeticOperation;
  /** Highest stage the player has unlocked (also the "current" highlight). */
  maxStage: number;
  preview: boolean;
}

/**
 * The progress bar shown beneath every VIF stage: a clickable line of stage
 * markers for the current operation, with completed/current/locked styling.
 *
 * Rewritten from the classic `VIFSequenceLine.jsfl` — which had no GameFrame
 * lifecycle — to read its configuration from props instead of URL parameters,
 * and without the (web-unused) PWA fetch branch.
 */
export function VifSequenceLine({
  operation,
  maxStage,
  preview,
}: VifSequenceLineProps) {
  return (
    <AnimationCanvas
      width={825}
      height={75}
      compositionId={COMPOSITION_ID}
      deps={[operation, maxStage, preview]}
      initGraphics={(cjs, an) => {
        VIFSequenceLine(cjs, an);
      }}
      onReady={({ lib, stage }) => {
        const exportRoot = new lib.VIFSequenceLine!();
        stage.addChild(exportRoot);
        draw(stage, exportRoot, { operation, maxStage, preview });
      }}
    />
  );
}

function draw(
  stage: createjs.Stage,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  exportRoot: any,
  { operation, maxStage, preview }: VifSequenceLineProps,
) {
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  /* eslint-disable @typescript-eslint/no-unsafe-argument */
  const sequenceLine = exportRoot.sequenceLine;
  const { slug } = $ArithmeticOperation.describe(operation);
  const operatorName = slug.charAt(0).toUpperCase() + slug.slice(1); // add -> Add
  const numStages =
    STAGES_BY_OPERATION[operation - 1] ?? STAGES_BY_OPERATION[0]!;
  const baseUrl = `${preview ? '/preview' : ''}/modules/vif`;

  UIUtil.hide(sequenceLine.opAdd);
  UIUtil.hide(sequenceLine.opSub);
  UIUtil.hide(sequenceLine.opMul);
  UIUtil.hide(sequenceLine.opDiv);

  const operatorGroup = sequenceLine['op' + operatorName];
  UIUtil.show(operatorGroup);

  for (let i = 1; i <= numStages; i++) {
    const button = operatorGroup[operatorName + 'Stage' + i];
    UIUtil.setFrame(
      button,
      i < maxStage
        ? FRAME_COMPLETED
        : i > maxStage
          ? FRAME_LOCKED
          : FRAME_CURRENT,
    );
    if (i <= maxStage) {
      UIUtil.createLink(button, `${baseUrl}/${slug}/${i}`);
    }
  }

  stage.update();

  /* eslint-enable @typescript-eslint/no-unsafe-member-access */
  /* eslint-enable @typescript-eslint/no-unsafe-assignment */
  /* eslint-enable @typescript-eslint/no-unsafe-argument */
}
